import React, { useState } from 'react';
import { Trash2, ArrowLeftRight } from 'lucide-react';

const ConnectionsSection = ({ connections, connectionTypes, people, currentPersonId, onChange }) => {
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [selectedType, setSelectedType] = useState(connectionTypes[0]?.value || 'associate');
  const [note, setNote] = useState('');

  // Set of "personId-type" pairs already connected — used to filter dropdown per type
  const connectedPairs = new Set(connections.map(c => `${c.person_id}-${c.type}`));

  // For the current selected type, filter out people already linked with that exact type
  const availablePeople = people.filter(p => {
    if (currentPersonId && p.id === currentPersonId) return false;
    return !connectedPairs.has(`${p.id}-${selectedType}`);
  });

  const handleTypeChange = (newType) => {
    setSelectedType(newType);
    // Reset person selection if current selection is no longer valid
    if (selectedPersonId) {
      const stillValid = !connectedPairs.has(`${selectedPersonId}-${newType}`) &&
        people.some(p => p.id === parseInt(selectedPersonId) && p.id !== currentPersonId);
      if (!stillValid) setSelectedPersonId('');
    }
  };

  const add = () => {
    const personId = parseInt(selectedPersonId);
    if (!personId) return;
    if (connectedPairs.has(`${personId}-${selectedType}`)) return;
    onChange([...connections, { person_id: personId, type: selectedType, note }]);
    setSelectedPersonId('');
    setNote('');
  };

  const remove = (i) => onChange(connections.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Connections
        {connections.length > 0 && (
          <span className="ml-2 text-xs text-gray-500 dark:text-slate-400 font-normal">
            ({connections.length})
          </span>
        )}
      </label>

      {/* Add row */}
      <div className="space-y-2 mb-3">
        <div className="flex space-x-2">
          {/* Type first — determines who appears in person dropdown */}
          <select
            value={selectedType}
            onChange={e => handleTypeChange(e.target.value)}
            className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100 dark:border-gray-600"
          >
            {connectionTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          {/* Person dropdown — filtered by selected type */}
          <select
            value={selectedPersonId}
            onChange={e => setSelectedPersonId(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100 dark:border-gray-600"
          >
            <option value="">
              {availablePeople.length === 0
                ? `All people already linked as "${connectionTypes.find(t => t.value === selectedType)?.label || selectedType}"`
                : 'Select a person…'}
            </option>
            {availablePeople.map(p => (
              <option key={p.id} value={p.id}>
                {p.first_name} {p.last_name || ''}
              </option>
            ))}
          </select>
        </div>

        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Connection notes (optional)"
          className="w-full px-3 py-2 border dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100"
        />
        <button
          type="button"
          onClick={add}
          disabled={!selectedPersonId}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add Connection
        </button>
      </div>

      {/* Connection list */}
      <div className="space-y-2">
        {connections.map((conn, i) => {
          const person = people.find(p => p.id === conn.person_id);
          const typeLabel = connectionTypes.find(t => t.value === conn.type)?.label || conn.type;
          return (
            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700">
              <div className="flex items-start space-x-2">
                <ArrowLeftRight className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-slate-100">
                    {person ? `${person.first_name} ${person.last_name || ''}` : `Person #${conn.person_id}`}
                  </span>
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                    {typeLabel}
                  </span>
                  {conn.note && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{conn.note}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-red-500 hover:text-red-700 transition-colors ml-3 shrink-0"
                title="Remove (will also remove from the other person)"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}

        {connections.length === 0 && (
          <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-2">
            No connections yet
          </p>
        )}
      </div>
    </div>
  );
};

export default ConnectionsSection;
